import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { customToastOptions } from '@/components/notifications/custom-toast-options';
import { notificationDetail } from '@/components/notifications/notification-detail';
import { NotificationToastCard } from '@/components/notifications/NotificationToastCard';
import { formatRelativeTime } from '@/lib/format';
import {
  useMarkNotificationsReadMutation,
  useNotificationsFeedQuery,
  useUnreadNotificationsCountQuery,
} from '@/services';

import { iconForCategory } from './notification-icons';

const INFO_TOAST_MS = 8000;

/**
 * The two things the inbox does without anyone opening the bell: toasts a new unread item as
 * it arrives, and carries the unread count into the tab title. Mounted once in `AdminLayout`.
 */
export function useNotificationEffects(): void {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const feed = useNotificationsFeedQuery();
  const { data: unread } = useUnreadNotificationsCountQuery();
  const { mutate: markRead } = useMarkNotificationsReadMutation();

  // The first load seeds this with every id already on the feed — nothing toasts for it. Every
  // load after that only toasts an id this ref hasn't seen yet.
  const seenIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    const items = feed.data?.pages.flatMap(page => page.items);
    if (!items) return;

    const seenIds = seenIdsRef.current;
    if (seenIds === null) {
      seenIdsRef.current = new Set(items.map(item => item.id));
      return;
    }

    for (const item of items) {
      if (seenIds.has(item.id)) continue;
      seenIds.add(item.id);
      if (item.readAt !== null) continue;

      if (item.urgency !== 'info') continue;

      toast.custom(
        id => (
          <NotificationToastCard
            tone="info"
            icon={iconForCategory(item.category)}
            title={t(`notifications.item.${item.category}.${item.type}`, item.params)}
            detail={notificationDetail(item, t)}
            time={formatRelativeTime(item.createdAt)}
            durationMs={INFO_TOAST_MS}
            onOpen={() => {
              markRead({ ids: [item.id] });
              navigate(item.href);
              toast.dismiss(id);
            }}
            onDismiss={() => toast.dismiss(id)}
          />
        ),
        customToastOptions(INFO_TOAST_MS)
      );
    }
  }, [feed.data, markRead, navigate, t]);

  const baseTitleRef = useRef(document.title);
  useEffect(() => {
    const base = baseTitleRef.current;
    const count = unread?.count ?? 0;
    document.title = count > 0 ? `(${count}) ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [unread?.count]);
}
